<template>
  <div class="page-wrap">
    <LoadingOverlay :visible="loading" message="Cargando instrumento..." />
    <LoadingOverlay :visible="loadingModificar" message="Cargando datos para modificar..." />

    <div v-if="!loading">
      <!-- Encabezado -->
      <header class="page-header">
        <h1>{{ tituloTipo }} — Datos de la actividad</h1>
      </header>

      <main class="page-body">

        <!-- Datos de la actividad -->
        <section v-if="instrumento" class="card">
          <div class="meta-grid">
            <div class="meta-item">
              <span class="meta-label">Fecha</span>
              <span class="meta-val">{{ formatearFecha(vigenciaDesde) }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">Grupos de Interés</span>
              <span class="meta-val">{{ metaEstamento || '—' }}</span>
            </div>
          </div>
        </section>

        <!-- Participantes y roles -->
        <section class="card">
          <h2>Participantes y roles</h2>
          <div class="table-wrap">
            <table class="participantes-table">
              <thead>
                <tr>
                  <th>Rol</th>
                  <th>Nombre</th>
                  <th>Cargo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="rol in rolesParticipantes" :key="rol">
                  <td class="td-rol">{{ rol }}</td>
                  <td></td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- Objetivo y tema -->
        <section v-if="instrumento || indicador" class="card">
          <h2>Objetivo y tema</h2>
          <div v-if="instrumento?.descripcion || objetivo" class="objetivo-block">
            <span class="meta-label">Objetivo</span>
            <p class="objetivo-text">{{ instrumento?.descripcion || objetivo }}</p>
          </div>
          <div v-if="indicador" class="indicador-chip">
            <span class="ind-badge">Indicador</span>
            <span class="ind-nombre">— {{ componente?.nombre ?? '—' }}</span>
          </div>
        </section>

        <!-- Grupos e preguntas -->
        <section v-if="gruposConPreguntas.length" class="card grupos-card">
          <div class="grupos-toolbar">
            <h2 class="grupos-title">Formulario</h2>
            <!-- Para formulario_completo: el indicador está a nivel de formulario, no de pregunta -->
            <div v-if="instrumento?.modalidad === 'formulario_completo' && indicador" class="ind-formulario-ref">
              <span class="ind-badge">Califica:</span>
              <span class="ind-nombre">{{ indicador.enunciado }}</span>
            </div>
            <div class="vista-toggle">
              <button
                :class="['vista-btn', { 'vista-btn--activo': vistaPreguntas === 'lista' }]"
                @click="vistaPreguntas = 'lista'"
              >Lista</button>
              <button
                :class="['vista-btn', { 'vista-btn--activo': vistaPreguntas === 'formulario' }]"
                @click="vistaPreguntas = 'formulario'"
              >Formulario</button>
            </div>
          </div>

          <div
            v-for="g in gruposConPreguntas"
            :key="g.id_grupo_interes"
            class="grupo-block"
          >
            <div class="grupo-header">
              <h3>{{ g.nombre || 'Grupo sin nombre' }}</h3>
              <span v-if="g.tipo_muestreo" class="meta-chip">{{ g.tipo_muestreo }}</span>
            </div>
            <p v-if="g.criterios" class="grupo-criterios">
              <strong>Criterios:</strong> {{ g.criterios }}
            </p>

            <div v-if="g.preguntas?.length" class="preguntas-panel">
              <div class="preguntas-head">
                <span class="preguntas-label">Preguntas</span>
                <span class="pill">{{ g.preguntas.length }}</span>
              </div>

              <div
                v-for="(p, idx) in g.preguntas"
                :key="p.id_pregunta || idx"
                class="q"
              >
                <div class="qh">
                  <span class="qn">{{ Number(idx) + 1 }}</span>
                  <span class="qt">{{ p.enunciado || p.pregunta }}</span>
                  <span v-if="p.tipo_respuesta || p.tipo" class="tag">
                    {{ etiquetaTipo(p.tipo_respuesta || p.tipo) }}
                  </span>
                  <template v-if="p.indicadores?.length">
                    <span
                      v-for="ind in p.indicadores"
                      :key="ind.id_indicador"
                      class="tag tag--ind"
                      :title="ind.enunciado"
                    >{{ ind.enunciado }}</span>
                  </template>
                  <span
                    v-else-if="p.id_indicador"
                    class="tag tag--ind"
                    :title="componente?.nombre ?? `Indicador #${p.id_indicador}`"
                  >{{ componente?.nombre ?? `Indicador #${p.id_indicador}` }}</span>
                </div>

                <!-- Vista lista (resumen) -->
                <template v-if="vistaPreguntas === 'lista'">
                <ul
                  v-if="Array.isArray(p.opciones) && p.opciones.length"
                  class="opciones"
                >
                  <li v-for="(o, i) in p.opciones" :key="i">{{ o }}</li>
                </ul>

                <div v-if="p.distribucion && esEncuesta" class="distribucion">
                  <div
                    v-for="(item, i) in normalizarDistribucion(p.distribucion)"
                    :key="i"
                    class="dist-row"
                  >
                    <span class="dist-opcion">{{ item.opcion }}</span>
                    <div class="dist-bar-wrap">
                      <div
                        class="dist-bar"
                        :style="{ width: item.pct + '%', background: colorOpcion(i, normalizarDistribucion(p.distribucion).length) }"
                      ></div>
                    </div>
                    <span class="dist-pct">{{ item.pct }}%</span>
                  </div>
                </div>

                <div
                  v-if="['abierta','long_text'].includes(p.tipo_respuesta || p.tipo)"
                  class="resp-show"
                >
                  <span class="muted">Respuesta abierta…</span>
                </div>

                <div v-if="(p.tipo_respuesta || p.tipo) === 'opinion'" class="tabla-opinion">
                  <table class="tabla-evaluacion">
                    <thead>
                      <tr>
                        <th>Variante</th>
                        <th v-for="(c, i) in p.criterios" :key="i">{{ c }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(v, i) in p.variantes" :key="i">
                        <td>{{ v }}</td>
                        <td v-for="(_, j) in p.criterios" :key="j">
                          <input type="checkbox" disabled />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                </template>

                <!-- Vista formulario (estilo Google Forms) -->
                <template v-else>
                  <div class="q-control">
                    <!-- Opción única -->
                    <div v-if="categoriaControl(p) === 'radio'" class="fp-opts">
                      <label v-for="(o, i) in opcionesLista(p)" :key="i" class="fp-opt">
                        <input type="radio" disabled /><span>{{ o }}</span>
                      </label>
                      <p v-if="!opcionesLista(p).length" class="fp-hint">Sin opciones definidas</p>
                    </div>
                    <!-- Casillas -->
                    <div v-else-if="categoriaControl(p) === 'checkbox'" class="fp-opts">
                      <label v-for="(o, i) in opcionesLista(p)" :key="i" class="fp-opt">
                        <input type="checkbox" disabled /><span>{{ o }}</span>
                      </label>
                      <p v-if="!opcionesLista(p).length" class="fp-hint">Sin opciones definidas</p>
                    </div>
                    <!-- Lista desplegable -->
                    <select v-else-if="categoriaControl(p) === 'select'" class="fp-select" disabled>
                      <option>Elegir…</option>
                      <option v-for="(o, i) in opcionesLista(p)" :key="i">{{ o }}</option>
                    </select>
                    <!-- Escala -->
                    <div v-else-if="categoriaControl(p) === 'escala'" class="fp-escala">
                      <span v-if="escalaInfo(p).min" class="fp-escala-lbl">{{ escalaInfo(p).min }}</span>
                      <label v-for="n in escalaInfo(p).rango" :key="n" class="fp-escala-item">
                        <span>{{ n }}</span><input type="radio" disabled />
                      </label>
                      <span v-if="escalaInfo(p).max" class="fp-escala-lbl">{{ escalaInfo(p).max }}</span>
                    </div>
                    <!-- Respuesta corta -->
                    <input v-else-if="categoriaControl(p) === 'corta'" type="text" class="fp-input" disabled placeholder="Texto de respuesta corta" />
                    <!-- Fecha -->
                    <input v-else-if="categoriaControl(p) === 'fecha'" type="date" class="fp-input fp-input-sm" disabled />
                    <!-- Hora -->
                    <input v-else-if="categoriaControl(p) === 'hora'" type="time" class="fp-input fp-input-sm" disabled />
                    <!-- Opinión (matriz) -->
                    <div v-else-if="categoriaControl(p) === 'opinion'" class="tabla-opinion">
                      <table class="tabla-evaluacion">
                        <thead>
                          <tr>
                            <th>Variante</th>
                            <th v-for="(c, i) in p.criterios" :key="i">{{ c }}</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="(v, i) in p.variantes" :key="i">
                            <td>{{ v }}</td>
                            <td v-for="(_, j) in p.criterios" :key="j"><input type="checkbox" disabled /></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <!-- Párrafo -->
                    <textarea v-else class="fp-textarea" disabled placeholder="Texto de respuesta larga"></textarea>
                  </div>
                </template>
              </div>
            </div>
            <p v-else class="sin-preguntas">No hay preguntas asignadas a este grupo.</p>
          </div>
        </section>

        <div v-else-if="tipoReconocido && !loading" class="card sin-grupos">
          <p>Este instrumento no tiene grupos de interés ni preguntas asignadas.</p>
        </div>

        <!-- Sin instrumento -->
        <div v-if="!instrumento && !loading" class="empty-state">
          <p>No se encontró información del instrumento.</p>
        </div>

        <!-- Botones -->
        <div class="botones">
          <button class="btn salir" @click="volver">Volver</button>
          <button
            v-if="authStore.tienePermiso('instrumentos.editar') && esOpinion"
            class="btn guardar"
            @click="modificarInstrumento"
          >Modificar instrumento</button>
        </div>
      </main>
    </div>

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />

    <!-- Modal de edición: el mismo InstrumentosView del árbol de indicadores -->
    <InstrumentosView
      :visible="mostrarModalInstrumento"
      :idTipoComponente="instrumento?.id_tipo_componente ?? 0"
      :idIndicador="relacion?.id_indicador ?? 0"
      :idInstrumento="instrumento?.id_instrumento ?? null"
      @cerrar="cerrarModalInstrumento"
    />

    <!-- Modal de modificación para formularios de opinión -->
    <ModificarFormularioModal
      :visible="mostrarModificarFormulario"
      :idInstrumento="instrumento?.id_instrumento ?? null"
      @cerrar="cerrarModalFormulario"
      @guardado="onFormularioGuardado"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * Detalle de un instrumento y sus preguntas.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { useRoute, useRouter } from 'vue-router'
import { ref, computed, onMounted } from 'vue'
import LoadingOverlay from './LoadingOverlay.vue'
import MensajeFlotante from './MensajeFlotante.vue'
import InstrumentosView from '@/views/InstrumentosView.vue'
import ModificarFormularioModal from './ModificarFormularioModal.vue'
import useAutenticar from '@/store/IngresoAutenticar'
import { useGestionInstrumento } from '@/store/GestionInstrumentos'
import { useGestionIndicadorInstrumentos } from '@/store/GestionIndicadorInstrumentos'


import { useGestionGrupoInteres } from '@/store/GestionGrupoInteres'
import { useGestionPreguntas } from '@/store/GestionPreguntas'
import { useGestionConfiguracionInstrumento } from '@/store/GestionConfiguracionInstrumento'
import { useGestionTipoComponente } from '@/store/GestionTipoComponente'
import { http } from '@/services/http'

const route = useRoute()
const router = useRouter()
const authStore = useAutenticar()

const instrumentoStore = useGestionInstrumento()
const relacionStore = useGestionIndicadorInstrumentos()


const grupoStore = useGestionGrupoInteres()
const preguntaStore = useGestionPreguntas()
const configStore = useGestionConfiguracionInstrumento()
const tipoComponenteStore = useGestionTipoComponente()

const instrumento = ref<any>(null)
const indicador = ref<any>(null)
const componente = ref<any>(null)
const relacion = ref<any>(null)
const gruposConPreguntas = ref<any[]>([])
const loading = ref(true)
const loadingModificar = ref(false)
const mostrarModalInstrumento = ref(false)
const mostrarModificarFormulario = ref(false)

// Usa el tipo del indicador (mismo criterio que FilaComponente.esIndicadorDeOpinion)
// para que la validación sea consistente en toda la app.
const esOpinion = computed(() => {
  if (!indicador.value?.id_tipo_componente) return false
  const tipo = (tipoComponenteStore.tipos ?? []).find(
    (t: any) => t.id_tipo_componente === indicador.value.id_tipo_componente
  )
  return /opini/i.test(String(tipo?.nombre ?? ''))
})
const vistaPreguntas = ref<'lista' | 'formulario'>('lista')

const tipo = computed(() => {
  const h = (instrumento.value?.herramienta || '').toLowerCase()
  if (h.includes('encuesta')) return 'encuesta'
  if (h.includes('entrevista')) return 'entrevista'
  if (h.includes('focal') || h.includes('focale')) return 'grupofocal'
  if (h.includes('taller') || h.includes('mesa')) return 'taller'
  return 'otro'
})

const esEncuesta     = computed(() => tipo.value === 'encuesta')
const tipoReconocido = computed(() => ['encuesta','entrevista','grupofocal','taller'].includes(tipo.value))

const tituloTipo = computed(() => {
  const map: Record<string, string> = {
    encuesta:   'Encuesta',
    entrevista: 'Entrevista',
    grupofocal: 'Grupo Focal',
    taller:     'Mesa-Taller',
    otro:       instrumento.value?.herramienta || 'Instrumento'
  }
  return map[tipo.value]
})

const rolesParticipantes = computed((): string[] => {
  if (tipo.value === 'entrevista') return ['Entrevistado', 'Entrevistador', 'Relator / Observador']
  if (tipo.value === 'grupofocal') return ['Moderador', 'Relator', 'Observador', 'Participante', 'Participante']
  if (tipo.value === 'taller')     return ['Facilitador', 'Relator', 'Participante', 'Observador']
  return ['Participante', 'Participante', 'Participante']
})

const metaEstamento = computed(() =>
  gruposConPreguntas.value.map(g => g.nombre).filter(Boolean).join(', ') || null
)

const actividad      = ref<any>(null)
const objetivo       = ref<string | null>(null)
const observaciones  = ref<string | null>(null)
const roles          = ref<any[]>([])
const participantes  = ref<any[]>([])
const vigenciaDesde  = ref<string | null>(null)

const mensajeVisible = ref(false)
const mensajeTexto   = ref('')
const mensajeTipo    = ref<'positivo' | 'error'>('positivo')

function formatearFecha(fecha: string | null): string {
  if (!fecha) return '—'
  const d = new Date(fecha)
  if (isNaN(d.getTime())) return fecha
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' })
}

function etiquetaTipo(t: string): string {
  const map: Record<string, string> = {
    single_choice:   'Opción única',
    opcion_unica:    'Opción única',
    opcion_multiple: 'Opción única',
    multiple_choice: 'Casillas',
    casillas:        'Casillas',
    checkbox:        'Casillas',
    desplegable:     'Lista desplegable',
    dropdown:        'Lista desplegable',
    opinion:         'Opinión',
    long_text:       'Párrafo',
    parrafo:         'Párrafo',
    texto:           'Párrafo',
    abierta:         'Abierta',
    respuesta_corta: 'Respuesta corta',
    numerica:        'Respuesta corta',
    escala_likert:   'Escala Likert',
    escala_lineal:   'Escala lineal',
    linear_scale:    'Escala lineal',
    rating:          'Escala lineal',
    fecha:           'Fecha',
    hora:            'Hora'
  }
  return map[t] || t
}

function opcionesLista(p: any): string[] {
  return Array.isArray(p?.opciones) ? p.opciones : []
}

/** Categoría de control para la previsualización estilo Google Forms. */
function categoriaControl(p: any): string {
  const t = p?.tipo_respuesta || p?.tipo || ''
  if (['single_choice', 'opcion_unica', 'opcion_multiple'].includes(t)) return 'radio'
  if (['multiple_choice', 'casillas', 'checkbox'].includes(t)) return 'checkbox'
  if (['desplegable', 'dropdown'].includes(t)) return 'select'
  if (['escala_likert', 'escala_lineal', 'linear_scale', 'rating'].includes(t)) return 'escala'
  if (['respuesta_corta', 'numerica'].includes(t)) return 'corta'
  if (['fecha', 'date'].includes(t)) return 'fecha'
  if (['hora', 'time'].includes(t)) return 'hora'
  if (t === 'opinion') return 'opinion'
  return 'parrafo'
}

/** Rango y etiquetas de una escala (likert fija 1-5 o lineal configurable). */
function escalaInfo(p: any): { rango: number[]; min: string; max: string } {
  const t = p?.tipo_respuesta || p?.tipo
  if (t === 'escala_likert') return { rango: [1, 2, 3, 4, 5], min: '', max: '' }
  const o = p?.opciones && !Array.isArray(p.opciones) ? p.opciones : {}
  const min = Number(o.min ?? 1)
  const max = Number(o.max ?? 5)
  const rango: number[] = []
  for (let n = min; n <= max && rango.length < 20; n++) rango.push(n)
  return {
    rango: rango.length ? rango : [1, 2, 3, 4, 5],
    min: o.etiqueta_min ?? '',
    max: o.etiqueta_max ?? '',
  }
}


function colorOpcion(idx: number, total: number): string {
  const pct = total > 1 ? Math.round((idx / (total - 1)) * 100) : 100
  if (pct >= 80) return '#2e7d32'
  if (pct >= 60) return '#558b2f'
  if (pct >= 40) return '#f9a825'
  if (pct >= 20) return '#e65100'
  return '#c62828'
}

function normalizarDistribucion(distribucion: any): Array<{ opcion: string; pct: number }> {
  if (!distribucion || typeof distribucion !== 'object') return []
  const entries = Object.entries(distribucion)
  const total = entries.reduce((s, [, v]) => s + Number(v), 0)
  if (!total) return []
  return entries.map(([k, v]) => ({ opcion: k, pct: Math.round((Number(v) / total) * 100) }))
}

function mapearPorTipo(inst: any) {
  if (!inst) return
  actividad.value     = inst.actividad ?? null
  objetivo.value      = inst.objetivo ?? null
  observaciones.value = inst.observaciones ?? null
  roles.value         = inst.roles ?? []
  participantes.value = inst.participantes ?? []
}

async function cargarDetalle() {
  loading.value = true
  const idInstrumento = Number(route.params.id)
  if (!idInstrumento) { loading.value = false; return }

  try {
    const inst = await instrumentoStore.getInstrumentoById(idInstrumento)
    if (!tipoComponenteStore.tipos.length) await tipoComponenteStore.getTipos()

    let gruposResult: any[] = []
    if (inst?.grupos_interes?.length) {
      await grupoStore.getGrupos()
      const todasPreguntas = await preguntaStore.getPreguntas({ id_instrumento: idInstrumento })

      // Si el backend no devuelve indicadores[] con enunciado, los cargamos
      // usando id_indicador. Se hace en paralelo para no serializar las llamadas.
      const idsIndicador = [...new Set(
        (todasPreguntas ?? [])
          .filter((p: any) => p.id_indicador && !(Array.isArray(p.indicadores) && p.indicadores.length))
          .map((p: any) => Number(p.id_indicador))
      )]
      const nombreIndicador: Record<number, string> = {}
      await Promise.all(idsIndicador.map(async (idInd) => {
        const r = await http.get<any>(`/api/indicadores/${idInd}`)
        if (r.ok) {
          const ind = r.data?.data ?? r.data
          if (ind?.enunciado) nombreIndicador[idInd] = ind.enunciado
        }
      }))

      let preguntasPorGrupoApi: any = null
      try { preguntasPorGrupoApi = await instrumentoStore.getPreguntasPorGrupo(idInstrumento) } catch { /* ignorar */ }

      gruposResult = inst.grupos_interes.map((g: any) => {
        const grupoInfo = grupoStore.grupos.find(
          (gr: any) => gr.id_grupo_interes === (g.id_grupo_interes ?? g.id)
        )
        const idGrupo = g.id_grupo_interes ?? g.id ?? grupoInfo?.id_grupo_interes
        let preguntasDelGrupo: any[] = []

        if (preguntasPorGrupoApi) {
          const apiGrupos = Array.isArray(preguntasPorGrupoApi)
            ? preguntasPorGrupoApi
            : (preguntasPorGrupoApi?.grupos ?? [])
          const found = apiGrupos.find(
            (gc: any) => (gc.id_grupo_interes ?? gc.grupo?.id_grupo_interes) === idGrupo
          )
          preguntasDelGrupo = found?.preguntas ?? []
        }

        if (!preguntasDelGrupo.length && todasPreguntas?.length) {
          // Las preguntas generales (id_grupo_interes null) aplican a todos los grupos.
          preguntasDelGrupo = todasPreguntas.filter(
            (p: any) => p.id_grupo_interes === idGrupo || p.id_grupo_interes == null
          )
        }

        if (!preguntasDelGrupo.length && (g.preguntas?.length || grupoInfo?.preguntas?.length)) {
          const embebidas = g.preguntas || grupoInfo?.preguntas || []
          preguntasDelGrupo = embebidas.filter(
            (p: any) => !p.instrumento || p.instrumento?.id_instrumento === idInstrumento
          )
        }

        return {
          id_grupo_interes: idGrupo,
          nombre:        grupoInfo?.nombre       ?? g.nombre       ?? `Grupo ${idGrupo}`,
          tipo_muestreo: grupoInfo?.tipo_muestreo ?? g.tipo_muestreo,
          criterios:     grupoInfo?.criterios     ?? g.criterios,
          preguntas: preguntasDelGrupo.map((p: any) => ({
            id_pregunta:   p.id_pregunta,
            enunciado:     p.enunciado    ?? p.pregunta,
            pregunta:      p.pregunta     ?? p.enunciado,
            tipo_respuesta: p.tipo_respuesta ?? p.tipo,
            tipo:          p.tipo          ?? p.tipo_respuesta,
            opciones:      p.opciones      ?? [],
            variantes:     p.variantes     ?? [],
            criterios:     p.criterios     ?? [],
            distribucion:  p.distribucion  ?? null,
            id_indicador:  p.id_indicador  ?? null,
            indicadores:   (() => {
              const existing = Array.isArray(p.indicadores) ? p.indicadores : []
              if (existing.length) return existing
              const idInd = p.id_indicador ?? null
              return idInd && nombreIndicador[idInd]
                ? [{ id_indicador: idInd, enunciado: nombreIndicador[idInd], id_version: 0 }]
                : []
            })()
          }))
        }
      })
    }

    gruposConPreguntas.value = gruposResult
    instrumento.value = inst

    await configStore.listar({ id_instrumento: idInstrumento })
    vigenciaDesde.value = configStore.configuraciones[0]?.vigencia_desde ?? null

    // Cargar componente: desde el query (navegación desde FilaComponente) o vía relación-indicador
    const idCompQuery = Number(route.query.id_componente)
    if (idCompQuery) {
      const rComp = await http.get<any>(`/api/componentes/${idCompQuery}`)
      componente.value = rComp.ok ? (rComp.data?.data ?? rComp.data ?? null) : null
    }

    if (!relacionStore.relaciones.length) await relacionStore.getRelaciones()
    const rel = relacionStore.relaciones.find(r => r.id_instrumento === idInstrumento)
    relacion.value = rel

    if (rel?.id_indicador) {
      const rInd = await http.get<any>(`/api/indicadores/${rel.id_indicador}`)
      const rawInd = rInd.ok ? (rInd.data?.data ?? rInd.data ?? null) : null
      indicador.value = rawInd

      if (!componente.value) {
        const idComp = rawInd?.id_componente ?? rawInd?.componente?.id_componente
        if (idComp) {
          const rComp = await http.get<any>(`/api/componentes/${idComp}`)
          componente.value = rComp.ok ? (rComp.data?.data ?? rComp.data ?? null) : null
        }
      }
    }

    mapearPorTipo(inst)
  } catch (e: any) {
    console.error('Error al cargar detalle del instrumento:', e)
    mensajeTexto.value  = 'Error al cargar el instrumento.'
    mensajeTipo.value   = 'error'
    mensajeVisible.value = true
  } finally {
    loading.value = false
  }
}

onMounted(cargarDetalle)

function modificarInstrumento() {
  if (!instrumento.value) return
  if (esOpinion.value) {
    mostrarModificarFormulario.value = true
  } else {
    mostrarModalInstrumento.value = true
  }
}

function onFormularioGuardado(mensaje: string) {
  mensajeTexto.value  = mensaje
  mensajeTipo.value   = 'positivo'
  mensajeVisible.value = true
}

async function cerrarModalInstrumento() {
  mostrarModalInstrumento.value = false
  await cargarDetalle()
}

async function cerrarModalFormulario() {
  mostrarModificarFormulario.value = false
  await cargarDetalle()
}

function volver() { router.back() }
</script>

<style scoped>
.page-wrap {
  --azul:      #0e69a0;
  --azul-cl:   #e8f1f8;
  --azul-txt:  #0c447c;
  --verde:     #2e7d32;
  --amarillo:  #eea222;
  --tinta:     #1e2a35;
  --mute:      #5b6b7a;
  --linea:     #d9e1ea;
  --bg:        #f4f7fa;

  background: var(--bg);
  border-radius: 14px;
  overflow: hidden;
  width: 95%;
  max-width: 1080px;
  margin: 20px auto;
  box-shadow: 0 4px 20px rgba(0,0,0,.12);
  font-family: var(--font-family, sans-serif);
}

/* Encabezado */
.page-header {
  background: var(--azul);
  color: #fff;
  padding: 18px 28px;
}
.page-header h1 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
}

/* Cuerpo */
.page-body { padding: 24px 28px 36px; }

/* Cards */
.card {
  background: #fff;
  border: 1px solid var(--linea);
  border-radius: 12px;
  padding: 20px 22px;
  margin-bottom: 20px;
  box-shadow: 0 1px 4px rgba(30,50,80,.05);
}
.card h2 {
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: 700;
  color: var(--tinta);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

/* Grid de metadatos de actividad */
.meta-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;
  border: 1px solid var(--linea);
  border-radius: 8px;
  overflow: hidden;
}
.meta-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-right: 1px solid var(--linea);
  border-bottom: none;
}
.meta-item:last-child { border-right: none; }
.meta-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: var(--mute);
}
.meta-val {
  font-size: 14px;
  color: var(--azul-txt);
  font-weight: 600;
  line-height: 1.4;
}

/* Tabla participantes */
.table-wrap { overflow-x: auto; }
.participantes-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.participantes-table th {
  background: var(--azul-cl);
  color: var(--azul-txt);
  font-weight: 700;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 10px 14px;
  text-align: left;
  border: 1px solid var(--linea);
}
.participantes-table td {
  border: 1px solid var(--linea);
  padding: 10px 14px;
  height: 36px;
  color: var(--tinta);
}
.td-rol {
  font-weight: 600;
  color: var(--tinta);
  width: 220px;
}

/* Objetivo */
.objetivo-block { margin-bottom: 14px; }
.objetivo-text {
  margin: 6px 0 0;
  font-size: 14px;
  color: var(--tinta);
  line-height: 1.6;
}

/* Chip del indicador */
.indicador-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  background: var(--azul-cl);
  border: 1px solid #b8d0e8;
  border-radius: 8px;
  padding: 8px 14px;
  flex-wrap: wrap;
}
.ind-badge {
  font-size: 12px;
  font-weight: 700;
  color: var(--azul-txt);
  white-space: nowrap;
}
.ind-nombre {
  font-size: 13px;
  color: var(--tinta);
  line-height: 1.4;
}

/* Indicador vinculado a formulario_completo */
.ind-formulario-ref {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  background: var(--azul-cl);
  border: 1px solid #b8d0e8;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 13px;
  flex-shrink: 0;
}

/* Grupos */
.grupos-card { padding-bottom: 8px; }
.grupo-block {
  border: 1px solid var(--linea);
  border-radius: 10px;
  padding: 14px 16px;
  margin-bottom: 14px;
  background: #fafcfe;
}
.grupo-header {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}
.grupo-header h3 { margin: 0; font-size: 15px; color: var(--azul); font-weight: 700; }
.meta-chip {
  background: var(--azul-cl);
  color: var(--azul-txt);
  border-radius: 12px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 600;
}
.grupo-criterios { margin: 0 0 10px; font-size: 13px; color: var(--mute); }
.sin-preguntas { color: var(--mute); font-style: italic; margin: 6px 0 0; font-size: 13px; }
.sin-grupos p { color: var(--mute); font-style: italic; margin: 0; }

/* Preguntas */
.preguntas-panel {
  background: #fff;
  border: 1px solid var(--linea);
  border-radius: 10px;
  padding: 14px 16px;
  margin-top: 12px;
}
.preguntas-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--linea);
}
.preguntas-label { font-size: 13px; font-weight: 700; color: var(--tinta); text-transform: uppercase; letter-spacing: 0.4px; }
.pill {
  display: inline-block;
  background: var(--azul-cl);
  color: var(--azul-txt);
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 12px;
  font-weight: 700;
}
.q {
  border: 1px solid var(--linea);
  border-radius: 10px;
  padding: 12px 14px;
  margin: 0 0 12px;
  background: #fafafa;
}
.qh { display: flex; gap: 8px; align-items: flex-start; margin-bottom: 8px; flex-wrap: wrap; }
.qn {
  background: var(--azul);
  color: #fff;
  border-radius: 6px;
  min-width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  flex: none;
}
.qt { font-weight: 600; color: var(--tinta); flex: 1; min-width: 160px; }
.tag {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  border-radius: 5px;
  padding: 2px 8px;
  background: var(--azul-cl);
  color: var(--azul-txt);
  white-space: nowrap;
  flex: none;
}
.tag--ind {
  background: #e6f4ea;
  color: #1b5e20;
  border: 1px solid #a5d6a7;
  font-weight: 600;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.opciones { margin: 6px 0 0; padding-left: 1.2rem; list-style: disc; }
.opciones li { margin-bottom: 4px; font-size: 14px; }
.resp-show {
  background: #f7fafc;
  border: 1px solid var(--linea);
  border-radius: 8px;
  padding: 8px 11px;
  font-size: 14px;
  margin-top: 6px;
}
.muted { color: var(--mute); font-style: italic; }

/* Distribución */
.distribucion { margin-top: 10px; display: flex; flex-direction: column; gap: 6px; }
.dist-row { display: grid; grid-template-columns: 120px 1fr 42px; gap: 8px; align-items: center; font-size: 13px; }
.dist-opcion { font-weight: 500; text-align: right; color: var(--tinta); }
.dist-bar-wrap { background: #e9ecef; border-radius: 4px; height: 14px; overflow: hidden; }
.dist-bar { height: 100%; border-radius: 4px; }
.dist-pct { font-weight: 700; color: var(--tinta); text-align: right; }

/* Tabla opinión */
.tabla-opinion { margin-top: 10px; overflow-x: auto; }
.tabla-evaluacion { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.tabla-evaluacion th,
.tabla-evaluacion td { border: 1px solid var(--linea); padding: 0.5rem 0.75rem; text-align: center; }
.tabla-evaluacion th {
  background: var(--azul-cl);
  color: var(--azul-txt);
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
}
.tabla-evaluacion td:first-child { text-align: left; font-weight: 500; background: #fafafa; }
.tabla-evaluacion input[type="checkbox"] { cursor: not-allowed; accent-color: var(--azul); }

/* Barra de vista (lista / formulario) */
.grupos-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.grupos-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--tinta);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.vista-toggle { display: inline-flex; border: 1px solid var(--linea); border-radius: 8px; overflow: hidden; }
.vista-btn {
  border: none; background: #fff; cursor: pointer;
  padding: 5px 14px; font-size: 12px; font-weight: 600;
  color: var(--mute); font-family: inherit;
}
.vista-btn:hover { background: var(--azul-cl); }
.vista-btn--activo { background: var(--azul); color: #fff; }

/* Acciones del form real */
.form-acciones { display: flex; gap: 6px; margin-left: auto; flex-wrap: wrap; }
.btn-mini {
  border: none; cursor: pointer;
  background: var(--azul); color: #fff;
  border-radius: 6px; padding: 4px 10px;
  font-size: 12px; font-weight: 600; font-family: inherit;
}
.btn-mini:hover { background: var(--azul-txt); }
.btn-mini--ghost { background: #fff; color: var(--azul); border: 1px solid var(--azul); }
.btn-mini--ghost:hover { background: var(--azul-cl); }

/* Previsualización estilo Google Forms */
.q-control { margin-top: 8px; padding-left: 34px; }
.fp-opts { display: flex; flex-direction: column; gap: 8px; }
.fp-opt { display: flex; align-items: center; gap: 8px; font-size: 14px; color: var(--tinta); }
.fp-opt input { accent-color: var(--azul); }
.fp-select {
  padding: 8px 10px; border: 1px solid var(--linea); border-radius: 8px;
  font-size: 14px; background: #fff; color: var(--tinta); max-width: 280px; font-family: inherit;
}
.fp-escala { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
.fp-escala-item { display: flex; flex-direction: column; align-items: center; gap: 4px; font-size: 13px; color: var(--tinta); }
.fp-escala-item input { accent-color: var(--azul); }
.fp-escala-lbl { font-size: 13px; color: var(--mute); font-weight: 600; }
.fp-input {
  padding: 8px 10px; border: none; border-bottom: 1px solid var(--linea);
  font-size: 14px; background: transparent; color: var(--mute); width: 100%; max-width: 340px; font-family: inherit;
}
.fp-input-sm { max-width: 170px; }
.fp-textarea {
  padding: 8px 10px; border: none; border-bottom: 1px solid var(--linea);
  font-size: 14px; background: transparent; color: var(--mute); width: 100%; max-width: 460px;
  resize: none; font-family: inherit;
}
.fp-hint { color: var(--mute); font-style: italic; font-size: 13px; margin: 0; }

/* Empty state */
.empty-state {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--mute);
  font-size: 15px;
  font-style: italic;
}

/* Botones */
.botones { display: flex; gap: 0.75rem; margin-top: 1.5rem; justify-content: flex-end; flex-wrap: wrap; }
.btn {
  padding: 0.65rem 1.4rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 0.92rem;
  font-family: inherit;
}
.btn.salir { background: #fff; border: 1px solid #ccc; color: #333; }
.btn.salir:hover { background: #d41a1a; color: #fff; border-color: #d41a1a; }
.btn.guardar { background: var(--amarillo); color: #000; }
.btn.guardar:hover { background: var(--azul); color: #fff; }

@media (max-width: 768px) {
  .page-body { padding: 16px; }
  .meta-grid { grid-template-columns: 1fr; }
  .meta-item { border-right: none; border-bottom: 1px solid var(--linea); }
  .meta-item:last-child { border-bottom: none; }
  .dist-row { grid-template-columns: 80px 1fr 36px; }
  .botones { flex-direction: column; align-items: stretch; }
}
</style>
